import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Aviso,
  Botao,
  Campo,
  CampoDeTexto,
  EstadoErro,
  EstadoVazio,
  Modal,
  Selo,
  Tabela,
  type ColunaTabela,
} from '../componentes/ui';
import { BotaoExtrairXlsx } from '../componentes/BotaoExtrairXlsx';
import { ErroDaApi } from './ErroDaApi';
import { requisicaoAutenticada } from '../servicos/http';
import type { ColunaDaPlanilha } from '../servicos/planilha';
import { Pagina } from './Carteira';

interface Servico {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly isActive: boolean;
}

/**
 * Catálogo de serviços da operadora.
 *
 * São os produtos que uma empresa pode contratar — hoje "Créditos ICMS" e
 * "Oportunidades Tributárias". A aba **Serviços Ativos** do perfil do cliente é
 * a outra ponta disto: lá se vê quais deles aquela empresa usa.
 */
const COLUNAS_DA_PLANILHA: readonly ColunaDaPlanilha<Servico>[] = [
  { titulo: 'Serviço', largura: 30, valor: (s) => s.name },
  { titulo: 'Descrição', largura: 60, valor: (s) => s.description },
  { titulo: 'Situação', largura: 10, valor: (s) => (s.isActive ? 'Ativo' : 'Inativo') },
];

export function Servicos(): JSX.Element {
  const [editando, setEditando] = useState<Servico | null>(null);
  const [criando, setCriando] = useState(false);

  const consulta = useQuery({
    queryKey: ['servicos'],
    queryFn: () => requisicaoAutenticada<Servico[]>('/business/servicos'),
  });

  const colunas: readonly ColunaTabela<Servico>[] = [
    { chave: 'name', titulo: 'Serviço', larguraMinima: '16rem', celula: (s) => s.name },
    {
      chave: 'description',
      titulo: 'Descrição',
      celula: (s) => (
        <span className="text-sm text-conteudo-secundario">{s.description ?? '—'}</span>
      ),
    },
    {
      chave: 'isActive',
      titulo: 'Situação',
      larguraMinima: '8rem',
      celula: (s) => (
        <Selo tom={s.isActive ? 'positivo' : 'neutro'}>{s.isActive ? 'Ativo' : 'Inativo'}</Selo>
      ),
    },
    {
      chave: 'acoes',
      titulo: 'Ações',
      tituloOculto: true,
      alinhamento: 'direita',
      larguraMinima: '6rem',
      celula: (s) => (
        <Botao variante="sutil" tamanho="pequeno" onClick={() => setEditando(s)}>
          Editar
        </Botao>
      ),
    },
  ];

  if (consulta.isError) {
    return (
      <Pagina titulo="Serviços">
        <EstadoErro
          titulo="Não foi possível carregar os serviços"
          mensagem={consulta.error.message}
          aoTentarNovamente={() => void consulta.refetch()}
        />
      </Pagina>
    );
  }

  return (
    <Pagina
      titulo="Serviços"
      descricao="O catálogo de produtos que uma empresa pode contratar. Liberar por empresa é na aba Serviços do cliente."
      contagem={consulta.data?.length}
      acao={
        <div className="flex flex-wrap items-center gap-3">
          <BotaoExtrairXlsx
            titulo="Serviços"
            disabled={consulta.data === undefined}
            colunas={COLUNAS_DA_PLANILHA}
            linhas={consulta.data ?? []}
          />
          <Botao onClick={() => setCriando(true)}>Novo serviço</Botao>
        </div>
      }
    >
      {!consulta.isLoading && (consulta.data?.length ?? 0) === 0 ? (
        <EstadoVazio titulo="Nenhum serviço" descricao="O catálogo está vazio." />
      ) : (
        <Tabela
          legenda="Catálogo de serviços"
          colunas={colunas}
          dados={consulta.data ?? []}
          chaveDaLinha={(s) => s.id}
          chaveDeCabecalhoDaLinha="name"
          carregando={consulta.isLoading}
        />
      )}

      <ModalDeServico
        servico={editando}
        criando={criando}
        aoFechar={() => {
          setEditando(null);
          setCriando(false);
        }}
      />
    </Pagina>
  );
}

/* ─── Edição ──────────────────────────────────────────────────────────────── */

/**
 * RN-312 — Criar ou editar um serviço do catálogo.
 *
 * ## Desativar não apaga
 *
 * Os vínculos das empresas apontam para o serviço; apagar a linha levaria junto
 * o histórico de quem o contratou. Serviço inativo some do que se pode liberar
 * — quem já tinha, continua com ele. A tela diz isso porque "inativo" costuma
 * ser lido como "sumiu".
 */
function ModalDeServico(props: {
  readonly servico: Servico | null;
  readonly criando: boolean;
  readonly aoFechar: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [ativo, setAtivo] = useState<boolean | null>(null);

  const salvar = useMutation({
    mutationFn: () =>
      requisicaoAutenticada<Servico>('/business/servicos', {
        metodo: 'PUT',
        corpo: {
          ...(props.servico === null ? {} : { id: props.servico.id }),
          name: nome === '' ? (props.servico?.name ?? '') : nome,
          description: descricao === '' ? (props.servico?.description ?? '') : descricao,
          isActive: ativo ?? props.servico?.isActive ?? true,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['servicos'] });
      fechar();
    },
  });

  const fechar = (): void => {
    salvar.reset();
    setNome('');
    setDescricao('');
    setAtivo(null);
    props.aoFechar();
  };

  const aberto = props.servico !== null || props.criando;
  const nomeAtual = nome === '' ? (props.servico?.name ?? '') : nome;
  const ativoAtual = ativo ?? props.servico?.isActive ?? true;

  return (
    <Modal
      key={props.servico?.id ?? 'novo'}
      aberto={aberto}
      aoFechar={fechar}
      titulo={props.servico === null ? 'Novo serviço' : 'Editar serviço'}
      rodape={
        <>
          <Botao variante="secundario" onClick={fechar}>
            Cancelar
          </Botao>
          <Botao
            onClick={() => salvar.mutate()}
            disabled={nomeAtual.trim() === ''}
            carregando={salvar.isPending}
          >
            Salvar
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Campo
          rotulo="Nome do serviço"
          required
          value={nomeAtual}
          onChange={(e) => setNome(e.target.value)}
        />
        <CampoDeTexto
          rotulo="Descrição"
          rows={3}
          ajuda="Aparece na aba de serviços do cliente, ao lado da caixa de seleção."
          value={descricao === '' ? (props.servico?.description ?? '') : descricao}
          onChange={(e) => setDescricao(e.target.value)}
        />

        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={ativoAtual}
            onChange={(e) => setAtivo(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-borda bg-superficie-base text-acento-ciano"
          />
          <span className="flex flex-col">
            <span className="text-sm text-conteudo-primario">Ativo no catálogo</span>
            <span className="text-xs text-conteudo-sutil">
              Desmarcado, ele deixa de poder ser liberado — quem já tem continua com ele.
            </span>
          </span>
        </label>

        {salvar.isError && <ErroDaApi titulo="Não foi possível salvar" erro={salvar.error} />}
      </div>
    </Modal>
  );
}
